import { useState } from "react";
import {
  Inbox, ListTree, Image as ImageIcon, BadgePoundSterling, ClipboardList,
  FileSignature, ShieldCheck, Send, MessageSquare, AlertCircle, ExternalLink,
  Mail, Phone, MapPin, ArrowUpRight, FileText, Download, Loader2, CheckCircle2,
  Hash, Receipt,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { STATUS_LABEL, STATUS_TONE, fmtMoney, fmtTime, relTime, shortId } from "./util";
import IntelligencePanel from "@/components/admin/IntelligencePanel";
import MarkupCard from "./MarkupCard";

const TABS = [
  { key: "communications", label: "Communications", icon: Inbox },
  { key: "overview",       label: "Overview",       icon: ClipboardList },
  { key: "timeline",       label: "Timeline",       icon: ListTree },
  { key: "evidence",       label: "Evidence",       icon: ImageIcon },
  { key: "quotes",         label: "Quotes",         icon: FileSignature },
  { key: "finance",        label: "Finance",        icon: BadgePoundSterling },
  { key: "audit",          label: "Audit",          icon: ShieldCheck },
];

export default function CenterTabs({ ws, onReload }) {
  const [tab, setTab] = useState("communications");
  return (
    <div className="bg-white border border-[#E2DCD0] rounded-lg overflow-hidden" data-testid="ws-center-tabs">
      <nav className="flex overflow-x-auto border-b border-[#E2DCD0] bg-[#F7F4ED]/60">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-[13px] whitespace-nowrap border-b-2 transition ${
                active
                  ? "border-[#064E2E] text-[#071A33] font-semibold bg-white"
                  : "border-transparent text-[#475569] hover:text-[#071A33] hover:bg-white/60"
              }`}
              data-testid={`ws-tab-${t.key}`}
            >
              <Icon size={14}/>
              {t.label}
              {t.key === "communications" && ws.notes.length > 0 && (
                <span className="bg-[#064E2E] text-white text-[10px] tracking-[0.06em] rounded-full px-1.5 py-0.5 font-semibold">{ws.notes.length}</span>
              )}
              {t.key === "evidence" && ws.evidence_summary.total > 0 && (
                <span className="bg-[#0F766E] text-white text-[10px] tracking-[0.06em] rounded-full px-1.5 py-0.5 font-semibold">{ws.evidence_summary.total}</span>
              )}
              {t.key === "finance" && (ws.invoices.length > 0 || ws.contractor_payments.length > 0) && (
                <span className="bg-[#B88A2A] text-white text-[10px] tracking-[0.06em] rounded-full px-1.5 py-0.5 font-semibold">{ws.invoices.length + ws.contractor_payments.length}</span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="p-4 sm:p-5">
        {tab === "communications" && <Communications ws={ws} onReload={onReload}/>}
        {tab === "overview"       && <Overview ws={ws}/>}
        {tab === "timeline"       && <Timeline ws={ws}/>}
        {tab === "evidence"       && <Evidence ws={ws}/>}
        {tab === "quotes"         && <Quotes ws={ws}/>}
        {tab === "finance"        && <Finance ws={ws} onReload={onReload}/>}
        {tab === "audit"          && <Audit ws={ws}/>}
      </div>
    </div>
  );
}

// ------ Communications -----------------------------------------------------
function Communications({ ws, onReload }) {
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("NOTE");
  const [busy, setBusy] = useState(false);

  async function addNote(e) {
    e?.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await adminApi.post(`/cases/${ws.case.id}/notes`, { body, kind });
      setBody("");
      toast.success("Note added");
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  }

  // Compose a unified timeline of notes + audit events, newest first.
  const stream = [
    ...ws.notes.map((n) => ({ kind: "NOTE", at: n.created_at, data: n })),
    ...ws.audit.map((a) => ({ kind: "AUDIT", at: a.at, data: a })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <div data-testid="ws-comms-pane">
      {/* Composer */}
      <form onSubmit={addNote} className="bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg p-3 mb-5" data-testid="ws-note-composer">
        <div className="flex gap-2 mb-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="field-input m-0 h-9 text-xs w-44" data-testid="ws-note-kind">
            <option value="NOTE">Internal note</option>
            <option value="CONTRACTOR_UPDATE">Contractor update</option>
            <option value="RESIDENT_UPDATE">Resident update</option>
          </select>
          <input
            id="ws-add-note-btn"
            data-testid="ws-add-note-btn"
            className="field-input m-0 h-9 text-sm flex-1"
            placeholder="Type a note or update…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <button type="submit" disabled={busy || !body.trim()} className="btn-primary h-9 py-0 px-4 text-sm" data-testid="ws-add-note-submit">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>} Add
          </button>
        </div>
        <p className="text-[10px] text-[#94A3B8]">Email + WhatsApp channels arrive once the comms gateway is wired — internal notes flow through here today.</p>
      </form>

      {stream.length === 0 ? (
        <EmptyState
          icon={<MessageSquare size={28} className="text-[#94A3B8]"/>}
          title="No communications yet"
          body="Internal notes, contractor updates and system events appear here as they happen."
        />
      ) : (
        <ol className="space-y-3" data-testid="ws-comms-stream">
          {stream.map((evt, i) => (
            <li key={`${evt.kind}-${evt.data.id}-${i}`} className="flex gap-3">
              <div className="shrink-0 w-2 h-2 rounded-full bg-[#064E2E] mt-2"/>
              <div className="flex-1 min-w-0 bg-white border border-[#E2DCD0] rounded-lg p-3" data-testid={`ws-comms-${evt.kind.toLowerCase()}-${evt.data.id}`}>
                <header className="flex items-baseline justify-between gap-3 mb-1.5">
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#B88A2A] font-medium">
                    {evt.kind === "NOTE" ? evt.data.kind.replace("_", " ") : evt.data.kind.replace(/_/g, " ")}
                    {evt.kind === "NOTE" && evt.data.author ? ` · ${evt.data.author}` : ""}
                  </span>
                  <time className="text-[10px] text-[#475569]" title={fmtTime(evt.at)}>{relTime(evt.at)}</time>
                </header>
                <p className="text-sm text-[#071A33] whitespace-pre-wrap leading-relaxed">
                  {evt.kind === "NOTE" ? evt.data.body : evt.data.summary}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Intelligence panel — anchored under the stream so reviewers always have the context. */}
      <div className="mt-6">
        <IntelligencePanel
          parkName={ws.case.park_name}
          town={ws.case.town_location}
          contractorName={ws.case.contractor_name}
          typeOfWorks={ws.case.type_of_works}
          description={ws.case.description}
          excludeCaseId={ws.case.id}
        />
      </div>
    </div>
  );
}

// ------ Overview -----------------------------------------------------------
function Overview({ ws }) {
  const c = ws.case;
  const fields = [
    ["Customer", c.full_name],
    ["Email", c.email ? <a href={`mailto:${c.email}`} className="text-[#0F766E] hover:underline">{c.email}</a> : null],
    ["Phone", c.phone ? <a href={`tel:${c.phone}`} className="text-[#0F766E] hover:underline">{c.phone}</a> : null],
    ["Park", c.park_name],
    ["Plot", c.plot_number],
    ["Town", c.town_location],
    ["Contractor", c.contractor_name],
    ["Type of works", c.type_of_works],
    ["Main concern", c.main_concern],
    ["Quote value", fmtMoney(c.quote_value)],
    ["High value", c.high_value ? "Yes" : "No"],
    ["External job ID", c.external_job_id],
    ["External system", c.external_system],
    ["Scheduled visit", c.scheduled_visit_at],
    ["Consented to contact", c.consent_to_contact ? "Yes" : "No"],
  ];
  return (
    <div className="space-y-5" data-testid="ws-overview-pane">
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
        {fields.map(([k, v]) => (
          <div key={k} className="border-b border-[#E2DCD0] pb-2 flex items-baseline justify-between gap-3">
            <dt className="text-xs text-[#475569]">{k}</dt>
            <dd className="text-sm text-[#071A33] text-right">{v == null || v === "" ? "—" : v}</dd>
          </div>
        ))}
      </dl>
      {c.description && (
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium mb-2">Description</p>
          <p className="text-sm leading-relaxed text-[#071A33] whitespace-pre-wrap bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg p-3">{c.description}</p>
        </div>
      )}
      {c.internal_notes && (
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium mb-2">Legacy internal notes</p>
          <p className="text-sm leading-relaxed text-[#071A33] whitespace-pre-wrap bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg p-3">{c.internal_notes}</p>
        </div>
      )}
    </div>
  );
}

// ------ Timeline -----------------------------------------------------------
function Timeline({ ws }) {
  // Read-only audit-style timeline. Includes derived events from existing case timestamps.
  const events = [];
  const c = ws.case;
  if (c.created_at)        events.push({ key: "created",  at: c.created_at, label: "Case created",          icon: AlertCircle });
  if (c.updated_at && c.updated_at !== c.created_at)
                           events.push({ key: "updated",  at: c.updated_at, label: "Case last updated",      icon: ClipboardList });
  if (c.exported_at)       events.push({ key: "exported", at: c.exported_at,label: `Exported to ${c.external_system || "external"}`, icon: ArrowUpRight });
  if (c.scheduled_visit_at)events.push({ key: "scheduled",at: c.scheduled_visit_at, label: "Visit scheduled", icon: ClipboardList });
  ws.audit.forEach((a) => events.push({ key: a.id, at: a.at, label: a.summary, icon: CheckCircle2 }));
  ws.invoices.forEach((i) => {
    if (i.sent_at) events.push({ key: `inv-sent-${i.id}`, at: i.sent_at, label: `Invoice ${i.invoice_number} sent (${fmtMoney(i.total ?? i.amount)})`, icon: FileText });
    if (i.paid_at) events.push({ key: `inv-paid-${i.id}`, at: i.paid_at, label: `Invoice ${i.invoice_number} paid`, icon: CheckCircle2 });
  });
  events.sort((a, b) => new Date(b.at) - new Date(a.at));

  if (events.length === 0) {
    return <EmptyState icon={<ListTree size={28} className="text-[#94A3B8]"/>} title="No timeline events yet" body="System events, status changes and finance milestones land here automatically."/>;
  }
  return (
    <ol className="relative pl-5" data-testid="ws-timeline-pane">
      <span className="absolute left-1 top-0 bottom-0 w-px bg-[#E2DCD0]" aria-hidden/>
      {events.map((e) => {
        const Icon = e.icon;
        return (
          <li key={e.key} className="relative pb-4 last:pb-0">
            <span className="absolute -left-[14px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#064E2E] ring-2 ring-white"/>
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-[#071A33] flex items-center gap-2"><Icon size={13} className="text-[#0F766E]"/>{e.label}</p>
              <time className="text-[10px] text-[#475569] whitespace-nowrap" title={fmtTime(e.at)}>{relTime(e.at)}</time>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ------ Evidence -----------------------------------------------------------
function Evidence({ ws }) {
  const files = ws.case.uploaded_files || [];
  if (files.length === 0) {
    return <EmptyState icon={<ImageIcon size={28} className="text-[#94A3B8]"/>} title="No evidence uploaded" body="Photos, documents and certificates submitted with the case appear here."/>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ws-evidence-pane">
      {files.map((f) => {
        const isImage = (f.content_type || "").startsWith("image/");
        return (
          <a
            key={f.id || f.url}
            href={f.url}
            target="_blank"
            rel="noreferrer"
            className="group relative aspect-[4/3] bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg overflow-hidden hover:border-[#064E2E] transition"
            data-testid={`ws-evidence-${f.id || f.url}`}
          >
            {isImage ? (
              <img src={f.url} alt={f.original_name || ""} className="w-full h-full object-cover"/>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-[#475569]">
                <FileText size={28}/>
                <span className="text-[10px] mt-2 uppercase tracking-[0.2em]">{(f.content_type || "file").split("/")[1]}</span>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
              <p className="text-[10px] text-white truncate">{f.original_name || f.url.split("/").pop()}</p>
            </div>
          </a>
        );
      })}
    </div>
  );
}

// ------ Quotes -------------------------------------------------------------
function Quotes({ ws }) {
  const c = ws.case;
  return (
    <div className="space-y-4" data-testid="ws-quotes-pane">
      <div className="bg-[#F7F4ED] border border-[#E2DCD0] rounded-lg p-4">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium mb-3">Contractor quote on file</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Row label="Contractor" value={c.contractor_name || "—"}/>
          <Row label="Quote value" value={<span className="font-display text-xl text-[#071A33] tabular-nums">{fmtMoney(c.quote_value)}</span>}/>
          <Row label="Type of works" value={c.type_of_works || "—"} colSpan={2}/>
          <Row label="Main concern" value={c.main_concern || "—"} colSpan={2}/>
        </div>
      </div>
      <IntelligencePanel
        parkName={c.park_name}
        town={c.town_location}
        contractorName={c.contractor_name}
        typeOfWorks={c.type_of_works}
        description={c.description}
        excludeCaseId={c.id}
      />
    </div>
  );
}

// ------ Finance ------------------------------------------------------------
function Finance({ ws, onReload }) {
  const f = ws.financials;
  const pricing = ws.pricing;

  const raiseInvoice = async () => {
    if (!pricing || pricing.client_price_ex_vat <= 0) {
      toast.error("Set the contractor cost and markup before invoicing");
      return;
    }
    if (!ws.case.email) {
      toast.error("Customer has no email on file — cannot send invoice");
      return;
    }
    try {
      const { data } = await adminApi.post(`/cases/${ws.case.id}/invoice?send=true`);
      toast.success(`Invoice ${data.invoice_number} created at ${fmtMoney(data.total)}`);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to raise invoice");
    }
  };

  return (
    <div className="space-y-5" data-testid="ws-finance-pane">
      {pricing && (
        <MarkupCard caseId={ws.case.id} pricing={pricing} onSaved={() => onReload?.()}/>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Contractor cost" value={fmtMoney(pricing?.contractor_cost_ex_vat ?? f.quote_value)} sub="ex VAT"/>
        <Stat label="Client price" value={fmtMoney(pricing?.client_price_ex_vat ?? f.quote_value)} sub="ex VAT" tone="positive"/>
        <Stat label="Customer paid" value={fmtMoney(f.paid_value)} tone={f.paid_value > 0 ? "positive" : "neutral"}/>
        <Stat label="Contractor paid" value={fmtMoney(f.contractor_paid)}/>
      </div>

      <Section
        title={`Invoices (${ws.invoices.length})`}
        action={pricing && pricing.client_price_ex_vat > 0 ? (
          <button onClick={raiseInvoice} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55]" data-testid="ws-invoice-from-case-btn">
            <Receipt size={12}/> Invoice client price
          </button>
        ) : null}
      >
        {ws.invoices.length === 0 ? (
          <p className="text-xs text-[#475569] italic">No invoices linked to this case.</p>
        ) : (
          <ul className="divide-y divide-[#E2DCD0]">
            {ws.invoices.map((i) => (
              <li key={i.id} className="py-2 flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><Hash size={12} className="text-[#475569]"/>{i.invoice_number}<span className="text-xs text-[#475569]">· {i.customer_name || "—"}</span></span>
                <span className="flex items-center gap-3">
                  <span className="tabular-nums">{fmtMoney(i.total ?? i.amount)}</span>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] rounded ${i.status === "PAID" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#B88A2A]/15 text-[#B88A2A]"}`}>{i.status}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={`Contractor payments (${ws.contractor_payments.length})`}>
        {ws.contractor_payments.length === 0 ? (
          <p className="text-xs text-[#475569] italic">None recorded.</p>
        ) : (
          <ul className="divide-y divide-[#E2DCD0]">
            {ws.contractor_payments.map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between text-sm">
                <span>{p.contractor_name}{p.reference && <span className="text-xs text-[#475569]"> · {p.reference}</span>}</span>
                <span className="flex items-center gap-3">
                  <span className="tabular-nums">{fmtMoney(p.amount)}</span>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] rounded ${p.status === "PAID" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#B88A2A]/15 text-[#B88A2A]"}`}>{p.status?.replace("_", " ")}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// ------ Audit --------------------------------------------------------------
function Audit({ ws }) {
  if (ws.audit.length === 0) {
    return <EmptyState icon={<ShieldCheck size={28} className="text-[#94A3B8]"/>} title="No audit entries" body="Every operator action is recorded here for compliance and traceability."/>;
  }
  return (
    <ol className="divide-y divide-[#E2DCD0]" data-testid="ws-audit-pane">
      {ws.audit.map((a) => (
        <li key={a.id} className="py-2.5 flex items-baseline justify-between gap-3 text-sm">
          <div>
            <p className="text-[10px] tracking-[0.18em] uppercase text-[#B88A2A]">{a.kind.replace(/_/g, " ")}</p>
            <p className="text-[#071A33]">{a.summary}</p>
          </div>
          <time className="text-[10px] text-[#475569] whitespace-nowrap" title={fmtTime(a.at)}>{relTime(a.at)}</time>
        </li>
      ))}
    </ol>
  );
}

// ------ Helpers ------------------------------------------------------------
function EmptyState({ icon, title, body }) {
  return (
    <div className="text-center py-10">
      {icon && <div className="mb-3">{icon}</div>}
      <p className="text-[#071A33] font-medium text-sm">{title}</p>
      {body && <p className="text-xs text-[#475569] mt-1 max-w-md mx-auto">{body}</p>}
    </div>
  );
}

function Row({ label, value, colSpan }) {
  return (
    <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569]">{label}</p>
      <p className="text-sm text-[#071A33] mt-0.5">{value}</p>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3">
        <h4 className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium">{title}</h4>
        {action}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone, sub }) {
  return (
    <div className={`border rounded-lg p-3 ${tone === "positive" ? "bg-[#064E2E]/5 border-[#064E2E]/30" : "bg-white border-[#E2DCD0]"}`}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569]">{label}</p>
      <p className="font-display text-xl text-[#071A33] tabular-nums mt-1">{value}</p>
      {sub && <p className="text-[10px] text-[#475569] mt-0.5">{sub}</p>}
    </div>
  );
}
